import { SESSION_PROPOSALS_ENABLED } from "../../utils/featureFlags";
import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { createBatchSessions, previewBatchSessions, SessionTemplate } from "../../utils/api";
import { MapPicker } from "./MapPicker";
import { ActivityType, User } from "../../types";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Command, CommandGroup, CommandItem } from "../ui/command";
import { Checkbox } from "../ui/checkbox";
import { Search, Footprints, Save, MapPin } from "lucide-react";
import CanicrossIcon from "../icon/CanicrossIcon";
import { ScrollArea } from "../ui/scroll-area";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import DetectionIcon from "../icon/DetectionIcon";

interface BatchSessionFormProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  dashboardId: string;
  dashboardTrainers: User[];
  onSessionsCreated: () => void;
}

interface ExtendedSessionTemplate extends Partial<SessionTemplate> {
  isRepeating?: boolean;
  repeatFrequency?: 'daily' | 'weekly' | 'monthly';
  repeatUntil?: string;
  showMap?: boolean;
}

const activityConfigs = {
  mantrailing: { label: "Mantrailing", icon: Search },
  hiking: { label: "Randonnée", icon: Footprints },
  canicross: { label: "Canicross", icon: CanicrossIcon },
  detection: { label: "Détection", icon: DetectionIcon }
};

export function BatchSessionForm({
  isOpen,
  onOpenChange,
  dashboardId,
  dashboardTrainers,
  onSessionsCreated,
}: BatchSessionFormProps) {
  const [numberOfSessions, setNumberOfSessions] = useState(1);
  const [sessionsData, setSessionsData] = useState<ExtendedSessionTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<{ count: number } | null>(null);

  useEffect(() => {
    if (isOpen) setPreview(null);
  }, [isOpen, sessionsData]);
  useEffect(() => {
    if (isOpen) {
      // Initialize with one default session when opening
      const now = new Date();
      const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
      setSessionsData([{
        startTime: now.toISOString().slice(0, 16),
        endTime: twoHoursLater.toISOString().slice(0, 16),
        location: "",
        activityType: "mantrailing",
        assignedTrainers: [],
        isRepeating: false,
        repeatFrequency: 'weekly',
        repeatUntil: '',
      }]);
      setNumberOfSessions(1);
    }
  }, [isOpen]);

  useEffect(() => {
    const diff = numberOfSessions - sessionsData.length;
    if (diff > 0) {
      const now = new Date();
      const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
      const newSessions = Array(diff).fill(null).map(() => ({
        startTime: now.toISOString().slice(0, 16),
        endTime: twoHoursLater.toISOString().slice(0, 16),
        location: "",
        activityType: "mantrailing",
        assignedTrainers: [],
        isRepeating: false,
        repeatFrequency: 'weekly',
        repeatUntil: '',
      })) as SessionTemplate[];
      setSessionsData(prev => [...prev, ...newSessions]);
    } else if (diff < 0) {
      setSessionsData(prev => prev.slice(0, numberOfSessions));
    }
  }, [numberOfSessions, sessionsData.length]);

  const handleSessionDataChange = (index: number, field: keyof ExtendedSessionTemplate, value: any) => {
    const updatedSessions = [...sessionsData];
    updatedSessions[index] = { ...updatedSessions[index], [field]: value };
    setSessionsData(updatedSessions);
  };

  const buildTemplates = () => {
    const templates = sessionsData.filter(t => t.startTime && t.endTime).map(({ showMap, ...template }) => template);
    if (templates.length === 0) {
      throw new Error("Veuillez configurer au moins une session valide avec une date de début et de fin.");
    }
    return templates as SessionTemplate[];
  };

  const handlePreview = async () => {
    try {
      setPreview(await previewBatchSessions(dashboardId, buildTemplates()));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "Aperçu impossible.");
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const created = await createBatchSessions(dashboardId, buildTemplates());
      toast.success(`${created.length} session(s) créée(s) avec succès !`);
      onSessionsCreated();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || (error instanceof Error ? error.message : "Erreur lors de la création des sessions."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl w-80%">
        <DialogHeader>
          <DialogTitle>Créer des sessions en lot</DialogTitle>
          <DialogDescription>Ajoutez et configurez plusieurs sessions en une seule fois.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="session-count">Nombre de modèles de session</Label>
            <Input id="session-count" type="number" value={numberOfSessions} onChange={(e) => setNumberOfSessions(Math.max(1, Number(e.target.value)))} min="1" className="w-32" />
          </div>

          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6">
              {sessionsData.map((session, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-4 bg-muted/50">
                  <h4 className="font-semibold">{`Modèle de session ${index + 1}`}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 items-end">
                    <div className="space-y-2"><Label>Début</Label><Input type="datetime-local" value={session.startTime} onChange={e => handleSessionDataChange(index, 'startTime', e.target.value)} /></div>
                    <div className="space-y-2"><Label>Fin</Label><Input type="datetime-local" value={session.endTime} onChange={e => handleSessionDataChange(index, 'endTime', e.target.value)} /></div>
                    <div className="space-y-2"><Label>Lieu</Label><Input value={session.location} onChange={e => handleSessionDataChange(index, 'location', e.target.value)} placeholder="Lieu de la session" /></div>
                    <div className="space-y-2 w-full">
                      <Label>Activité</Label> 
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-start font-normal">{React.createElement(activityConfigs[session.activityType as ActivityType]?.icon || Search, { className: "mr-2 h-4 w-4" })}<span>{activityConfigs[session.activityType as ActivityType]?.label || 'Sélectionner'}</span></Button></DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {Object.entries(activityConfigs).map(([key, { label, icon }]) => (
                            <DropdownMenuItem key={key} onSelect={() => handleSessionDataChange(index, 'activityType', key as ActivityType)}>{React.createElement(icon, { className: "mr-2 h-4 w-4" })}<span>{label}</span></DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu> 
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="space-y-2"><Label>Titre</Label><Input value={session.title || ""} onChange={e => handleSessionDataChange(index, 'title', e.target.value)} placeholder="Optionnel" /></div>
                    <div className="space-y-2"><Label>Chiens max</Label><Input type="number" min={1} max={500} value={session.maxDogs ?? ""} onChange={e => handleSessionDataChange(index, 'maxDogs', e.target.value ? Number(e.target.value) : undefined)} placeholder="Illimité" /></div>
                    {SESSION_PROPOSALS_ENABLED && <label className="flex items-center gap-2 text-sm pb-2">
                      <input type="checkbox" checked={session.status === 'published'} onChange={e => handleSessionDataChange(index, 'status', e.target.checked ? 'published' : 'draft')} />
                      Publier directement
                    </label>}
                  </div>
                  <div className="space-y-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => handleSessionDataChange(index, 'showMap', !session.showMap)}>
                      <MapPin className="mr-2 h-4 w-4" />{session.locationCoordinate ? "Point GPS défini" : "Ajouter un point GPS"}
                    </Button>
                    {session.showMap && (
                      <MapPicker
                        value={session.locationCoordinate}
                        onChange={point => handleSessionDataChange(index, 'locationCoordinate', point || undefined)}
                        onAddressFound={label => !session.location && handleSessionDataChange(index, 'location', label)}
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                     <div className="space-y-2 w-full">
                      <Label>Éducateurs</Label>
                      <Popover>
                        <PopoverTrigger asChild><Button variant="outline" className="w-full justify-start font-normal truncate">{session.assignedTrainers && session.assignedTrainers.length > 0 ? `${session.assignedTrainers.length} sélectionné(s)` : "Sélectionner..."}</Button></PopoverTrigger>
                        <PopoverContent className="p-0"><Command><CommandGroup>
                          {dashboardTrainers.map(trainer => (
                            <CommandItem key={trainer._id} onSelect={() => {
                              const currentAssigned = session.assignedTrainers || [];
                              const newAssigned = currentAssigned.includes(trainer._id) ? currentAssigned.filter(id => id !== trainer._id) : [...currentAssigned, trainer._id];
                              handleSessionDataChange(index, 'assignedTrainers', newAssigned);
                            }}>
                              <Checkbox checked={session.assignedTrainers?.includes(trainer._id)} className="mr-2" />
                              <span>{trainer.username}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup></Command></PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="p-3 border rounded-md bg-background/50 space-y-3">
                     <div className="flex items-center space-x-2">
                      <Switch id={`repeat-switch-${index}`} checked={session.isRepeating} onCheckedChange={(checked) => handleSessionDataChange(index, 'isRepeating', checked)} />
                      <Label htmlFor={`repeat-switch-${index}`}>Répéter cette session</Label>
                    </div>
                    {session.isRepeating && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fréquence</Label>
                          <Select value={session.repeatFrequency} onValueChange={(v) => handleSessionDataChange(index, 'repeatFrequency', v as any)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Chaque jour</SelectItem>
                              <SelectItem value="weekly">Chaque semaine</SelectItem>
                              <SelectItem value="monthly">Chaque mois</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Répéter jusqu'au</Label>
                          <Input type="date" value={session.repeatUntil} onChange={e => handleSessionDataChange(index, 'repeatUntil', e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
        <DialogFooter className="items-center">
          {preview && <span className="mr-auto text-sm text-muted-foreground">{preview.count} session(s) seront créées</span>}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Annuler</Button>
          <Button variant="secondary" onClick={handlePreview} disabled={isLoading}>Aperçu</Button>
          <Button onClick={handleSubmit} disabled={isLoading}><Save className="mr-2 h-4 w-4" />{isLoading ? "Création..." : `Créer les sessions`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
